import { socketMessageReceived, wsConnected, wsDisconnected } from "../websockets/actions"
import { WS_CONNECT, WS_DISCONNECT } from "../websockets/types"
import { getAuthHeaders } from "../websocket-utils"

const RECONNECT_BASE_DELAY_MS = 2000 // 2 seconds
const RECONNECT_MAX_DELAY_MS = 60000 // 60 seconds

const socketMiddleware = () => {
    // Connections are keyed by host so that features wanting different channels can hold them at
    // the same time. A single slot meant opening one channel silently evicted another — the TK
    // export side rail and the weekly view live on the same page and would fight over it.
    const sockets = {}
    // Reconnect bookkeeping is per host, so one channel backing off never resets another's state.
    const reconnectState = {}

    const stateFor = host => {
        if (!reconnectState[host]) reconnectState[host] = { retryCount: 0, reconnectTimer: null, isGuest: false }
        return reconnectState[host]
    }

    const clearReconnectTimer = host => {
        const state = reconnectState[host]
        if (state?.reconnectTimer !== null && state?.reconnectTimer !== undefined) {
            clearTimeout(state.reconnectTimer)
            state.reconnectTimer = null
        }
    }

    const closeSocket = host => {
        clearReconnectTimer(host)
        if (sockets[host]) sockets[host].close()
        delete sockets[host]
        delete reconnectState[host]
    }

    // Handlers below take the specific socket instance they were bound to and bail if it's no
    // longer the one tracked for the host. Without that guard, a stale event from a socket that
    // was already replaced (e.g. closeSocket followed by an immediate reconnect) could delete or
    // dispatch on behalf of the newer connection.
    const onOpen = (store, host, socket) => () => {
        if (sockets[host] !== socket) return
        stateFor(host).retryCount = 0
        store.dispatch(wsConnected(host, socket))
    }
    const onClose = (store, host, socket) => event => {
        if (sockets[host] !== socket) return
        // Reconnect on unclean closes (abrupt drop) or server-side transient codes:
        //   1001 Going Away, 1011 Internal Error, 1012 Service Restart,
        //   1013 Try Again Later, 1014 Bad Gateway.
        // Code 1000 is a normal intentional close — do not reconnect.
        if (!event.wasClean || [1001, 1011, 1012, 1013, 1014].includes(event.code)) {
            const state = stateFor(host)
            const delay = Math.min(RECONNECT_BASE_DELAY_MS * Math.pow(2, state.retryCount), RECONNECT_MAX_DELAY_MS)
            state.retryCount++
            state.reconnectTimer = setTimeout(() => connectSocket(host, store, state.isGuest, true), delay)
        } else {
            delete sockets[host]
            store.dispatch(wsDisconnected(host))
        }
    }
    const onMessage = (store, host, socket) => event => {
        if (sockets[host] !== socket) return
        store.dispatch(socketMessageReceived(event))
    }

    const connectSocket = (host, store, isGuest = false, isReconnect = false) => {
        const state = stateFor(host)
        if (!isReconnect) {
            state.retryCount = 0
            state.isGuest = isGuest
        }
        // if we already have a socket open for this host, we don't need another one
        const existing = sockets[host]
        if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
            return
        }
        const socket = new WebSocket(host, getAuthHeaders(isGuest))
        socket.onclose = onClose(store, host, socket)
        socket.onopen = onOpen(store, host, socket)
        socket.onmessage = onMessage(store, host, socket)
        socket.keepAlive = true
        sockets[host] = socket
    }
    // the middleware part of this function
    return store => next => action => {
        switch (action.type) {
            case WS_CONNECT:
                if (!action.host) {
                    // if we don't have a host to connect to, we can't do anything
                    break
                }
                connectSocket(action.host, store, !!action.isGuest)
                break
            case WS_DISCONNECT:
                // A host closes just that channel. Without one, close them all — callers that
                // predate keyed connections rely on that, and so does logging out.
                // closeSocket drops its entry from `sockets` before the close event fires, so the
                // stale-instance guard in onClose swallows it. Announce the disconnect here instead,
                // otherwise the store keeps a reference to a closed socket and callers that check
                // for one before connecting never reconnect.
                if (action.host) {
                    closeSocket(action.host)
                    store.dispatch(wsDisconnected(action.host))
                } else {
                    const openHosts = Object.keys(sockets)
                    openHosts.forEach(host => closeSocket(host))
                    // Only announce it if there was something to close, so a disconnect with
                    // nothing open doesn't clear unrelated notification state.
                    if (openHosts.length) store.dispatch(wsDisconnected())
                }
                break
            default:
                // this is middleware, so we need to pass the action along if it isn't the one we want
                return next(action)
        }
    }
}
export default socketMiddleware()
